import { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, Phone, MapPin, Mail, ArrowRight, LogIn } from "lucide-react";
import { useSite } from "../../lib/site";
import { useAuth, homeFor } from "../../lib/auth";
import { track } from "../../lib/api";
import { useEffect } from "react";

const NAV = [["Programs", "/programs"], ["Classes", "/classes"], ["Admissions", "/admissions"], ["Funding", "/funding"], ["About", "/about"], ["FAQ", "/faq"], ["Contact", "/contact"]];

export function Logo({ className = "h-11" }) {
  const { site } = useSite();
  return site.logo_url ? <img src={site.logo_url} alt={`${site.org_name || "Healthcare Resources"} logo`} className={`${className} w-auto object-contain`} width="180" height="88" />
    : <span className="font-heading text-xl font-extrabold">HR <span className="text-red-600">Healthcare Resources</span></span>;
}

function Header() {
  const [open, setOpen] = useState(false);
  const { site } = useSite();
  const { user } = useAuth();
  const loc = useLocation();
  useEffect(() => setOpen(false), [loc.pathname]);
  return (
    <header className="sticky top-0 z-40 border-b border-slate-100 bg-white/95 backdrop-blur-md">
      <div className="hidden bg-slate-900 text-xs text-slate-300 md:block">
        <div className="container-x flex h-9 items-center justify-between">
          <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-red-400" />{site.address}, {site.city}, {site.state} {site.zip}</span>
          <div className="flex items-center gap-5">
            <a href={`tel:${site.phone}`} className="flex items-center gap-1.5 hover:text-white" data-testid="topbar-phone"><Phone className="h-3.5 w-3.5 text-red-400" />{site.phone}</a>
            <a href={`mailto:${site.email}`} className="flex items-center gap-1.5 hover:text-white"><Mail className="h-3.5 w-3.5 text-red-400" />{site.email}</a>
          </div>
        </div>
      </div>
      <div className="container-x flex h-[72px] items-center justify-between gap-4">
        <Link to="/" data-testid="header-logo-link" aria-label="Healthcare Resources home"><Logo /></Link>
        <nav className="hidden items-center gap-1 lg:flex" aria-label="Main">
          {NAV.map(([l, h]) => (
            <NavLink key={h} to={h} data-testid={`nav-${l.toLowerCase()}`} className={({ isActive }) => `rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${isActive ? "text-red-600" : "text-slate-700 hover:text-red-600"}`}>{l}</NavLink>
          ))}
        </nav>
        <div className="hidden items-center gap-2 lg:flex">
          <Link to={user ? homeFor(user) : "/login"} className="btn-outline btn-sm" data-testid="header-login-btn"><LogIn className="h-4 w-4" />{user ? "My Portal" : "Login"}</Link>
          <Link to="/apply" onClick={() => track("cta_click", { label: "header_apply" })} className="btn-primary btn-sm" data-testid="header-apply-btn">Apply Now</Link>
        </div>
        <button className="rounded-lg p-2 lg:hidden" onClick={() => setOpen(!open)} aria-label="Toggle menu" aria-expanded={open} data-testid="mobile-menu-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <nav className="border-t border-slate-100 bg-white px-4 pb-5 pt-2 lg:hidden" data-testid="mobile-menu">
          {NAV.concat([["Faculty & Staff", "/faculty"], ["Events", "/events"], ["Blog", "/blog"], ["Find Your Program", "/find-your-program"]]).map(([l, h]) => (
            <Link key={h} to={h} className="block border-b border-slate-50 py-3 font-semibold text-slate-800">{l}</Link>
          ))}
          <Link to={user ? homeFor(user) : "/login"} className="btn-outline mt-4 w-full">{user ? "My Portal" : "Login"}</Link>
        </nav>
      )}
    </header>
  );
}

function Footer() {
  const { site } = useSite();
  const col = (title, links) => (
    <div>
      <h3 className="mb-4 text-sm font-bold uppercase tracking-wider text-white">{title}</h3>
      <ul className="space-y-2.5 text-sm">{links.map(([l, h]) => <li key={h + l}><Link to={h} className="hover:text-white">{l}</Link></li>)}</ul>
    </div>
  );
  return (
    <footer className="bg-slate-950 pb-24 pt-16 text-slate-400 md:pb-10" data-testid="site-footer">
      <div className="container-x grid gap-10 md:grid-cols-2 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <div className="inline-block rounded-xl bg-white p-2"><Logo className="h-12" /></div>
          <address className="mt-5 space-y-1.5 text-sm not-italic">
            <p className="font-bold text-white">{site.org_name}</p>
            <p>{site.address}<br />{site.city}, {site.state} {site.zip}</p>
            <p><a href={`tel:${site.phone}`} className="hover:text-white" data-testid="footer-phone">{site.phone}</a> · Fax {site.fax}</p>
            <p><a href={`mailto:${site.email}`} className="hover:text-white" data-testid="footer-email">{site.email}</a></p>
          </address>
        </div>
        {col("Quick Links", [["Programs", "/programs"], ["Admissions", "/admissions"], ["Upcoming Classes", "/classes"], ["Funding", "/funding"], ["Faculty & Staff", "/faculty"], ["FAQ", "/faq"], ["Contact", "/contact"]])}
        {col("Resources", [["Find Your Program", "/find-your-program"], ["Student Success", "/student-success"], ["Events", "/events"], ["Blog", "/blog"], ["Funding", "/funding"], ["About", "/about"]])}
        {col("Portals", [["Student Login", "/login?portal=student"], ["Staff Login", "/login?portal=staff"], ["Admin Login", "/login?portal=admin"], ["Privacy Policy", "/privacy"], ["Terms", "/terms"]])}
      </div>
      <div className="container-x mt-12 border-t border-slate-800 pt-6 text-xs">© {new Date().getFullYear()} {site.org_name}. Serving {site.service_area}.</div>
    </footer>
  );
}

function MobileApplyBar() {
  const { site } = useSite();
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 flex items-center gap-2 border-t border-slate-200 bg-white/95 p-3 backdrop-blur-md md:hidden" data-testid="mobile-sticky-bar">
      <a href={`tel:${site.phone}`} className="btn-outline btn-sm flex-1" data-testid="mobile-call-btn"><Phone className="h-4 w-4" />Call</a>
      <Link to="/apply" onClick={() => track("cta_click", { label: "mobile_apply" })} className="btn-primary btn-sm flex-[2]" data-testid="mobile-apply-btn">Apply Now <ArrowRight className="h-4 w-4" /></Link>
    </div>
  );
}

export default function PublicLayout() {
  const loc = useLocation();
  useEffect(() => { window.scrollTo(0, 0); track("page_view"); }, [loc.pathname]);
  return (
    <div className="flex min-h-screen flex-col">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50 focus:rounded focus:bg-white focus:p-2">Skip to content</a>
      <Header />
      <main id="main" className="flex-1"><Outlet /></main>
      <Footer />
      <MobileApplyBar />
    </div>
  );
}
